import {
  ArrowRight,
  Check,
  Circle,
  Clock3,
  Link2,
  MoreHorizontal,
  Sparkles,
  Trash2
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { EditTaskModal } from "./EditTaskModal";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { eventLabels, formatDateTime, relativeDue, statusLabels } from "../lib/format";
import type { ActivityEvent, Project, Task } from "../types";
import { Button, StatusPill } from "./ui";

export function TaskRow({ task, showReason = false }: { task: Task; showReason?: boolean }) {
  const { mutate, refresh } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const isDone = task.status === "COMPLETED";
  const due = relativeDue(task.due_date);

  const complete = async () => {
    const result = await mutate(() => api.completeTask(task.id), "任务已完成，联动结果已写入时间轴");
    if (result && result.xp > 0) {
      // The refresh and base confirmation already happened in mutate; XP remains visible in the skill page.
    }
  };

  const changeStatus = async (status: string) => {
    setMenuOpen(false);
    await mutate(() => api.updateTaskStatus(task.id, status), "任务状态已更新");
  };

  const remove = async () => {
    setMenuOpen(false);
    await mutate(() => api.remove("tasks", task.id), "任务已移入回收站");
  };

  return (
    <div className={`task-row ${isDone ? "task-row--done" : ""}`}>
      <button
        className="task-row__check"
        aria-label={isDone ? "任务已完成" : "完成任务"}
        onClick={() => !isDone && void complete()}
        disabled={isDone}
      >
        {isDone ? <Check size={15} /> : <Circle size={16} />}
      </button>
      <div className="task-row__body">
        <div className="task-row__title">
          <strong>{task.title}</strong>
          {task.pinned ? <span className="mini-tag mini-tag--lime">置顶</span> : null}
        </div>
        <div className="task-row__meta">
          {task.project_title && <span><Link2 size={12} />{task.project_title}</span>}
          {due && <span className={due.includes("逾期") ? "overdue" : ""}><Clock3 size={12} />{due}</span>}
          {task.estimated_minutes && <span>{task.estimated_minutes} 分钟</span>}
          {task.skill_title && <span><Sparkles size={12} />{task.skill_title}</span>}
          {task.contact_name && <span><Link2 size={12} />{task.contact_name}</span>}
        </div>
        {showReason && task.explanations.length > 0 && (
          <div className="task-row__reason">
            <Sparkles size={13} />
            {task.explanations.slice(0, 2).join(" · ")}
          </div>
        )}
      </div>
      <StatusPill status={task.status}>{statusLabels[task.status] ?? task.status}</StatusPill>
      <div className="row-menu">
          <button onClick={() => setMenuOpen((open) => !open)} aria-label="任务操作">
            <MoreHorizontal size={18} />
          </button>
          {menuOpen && (
            <div className="row-menu__popover">
              <button onClick={() => { setMenuOpen(false); setEditing(true); }}>编辑任务</button>
              {!isDone && task.status !== "IN_PROGRESS" && <button onClick={() => void changeStatus("IN_PROGRESS")}>设为进行中</button>}
              {!isDone && task.status !== "WAITING_EXTERNAL" && <button onClick={() => void changeStatus("WAITING_EXTERNAL")}>等待外部</button>}
              {!isDone && task.status !== "BLOCKED" && <button onClick={() => void changeStatus("BLOCKED")}>标记受阻</button>}
              {!isDone && <button onClick={() => void changeStatus("CANCELLED")}>取消任务</button>}
              <button onClick={() => void remove()}>移入回收站</button>
            </div>
          )}
      </div>
      {editing && <EditTaskModal task={task} onClose={() => setEditing(false)} />}
    </div>
  );
}

const eventTone: Record<string, string> = {
  ACTION: "blue",
  PROGRESS: "violet",
  RESULT: "lime",
  OUTCOME: "lime",
  DECISION: "amber",
  ISSUE: "red",
  FEEDBACK: "cyan",
  LEARNING: "violet",
  APPLICATION: "blue",
  REVIEW: "slate",
  MILESTONE: "amber",
  STATUS: "slate"
};

export function EventItem({ event, last = false }: { event: ActivityEvent; last?: boolean }) {
  const { mutate } = useApp();
  return (
    <article className={`event-item ${last ? "event-item--last" : ""}`}>
      <div className="event-item__rail">
        <span className={`event-item__dot event-item__dot--${eventTone[event.event_type] ?? "slate"}`} />
        {!last && <i />}
      </div>
      <div className="event-item__content">
        <div className="event-item__top">
          <div>
            <span className="event-type">{eventLabels[event.event_type] ?? event.event_type}</span>
            <time>{formatDateTime(event.occurred_at)}</time>
          </div>
          {event.ai_assisted ? <span className="mini-tag">AI 辅助整理</span> : null}
          <button
            className="event-item__delete"
            onClick={() => void mutate(() => api.remove("activity_events", event.id), "时间线记录已移入回收站")}
            aria-label="删除时间线记录"
          >
            <Trash2 size={14} />
          </button>
        </div>
        <h3>{event.title}</h3>
        {event.factual_content && <p>{event.factual_content}</p>}
        {(event.result_summary || event.next_action) && (
          <div className="event-item__details">
            {event.result_summary && <span><b>结果</b>{event.result_summary}</span>}
            {event.next_action && <span><b>下一步</b>{event.next_action}</span>}
          </div>
        )}
      </div>
    </article>
  );
}

export function ProjectCard({ project }: { project: Project }) {
  const { mutate } = useApp();
  const progress = project.progress;
  const remove = () => mutate(() => api.remove("projects", project.id), "项目已移入回收站");
  return (
    <article className="project-card" id={`project-${project.id}`}>
      <div className="project-card__top">
        <div>
          {project.goal_title && <span className="eyebrow">{project.goal_title}</span>}
          <h3>{project.title}</h3>
        </div>
        <StatusPill status={project.risk}>
          {project.risk === "NONE" ? statusLabels[project.status] : statusLabels[project.risk]}
        </StatusPill>
      </div>
      <p>{project.outcome || "还没有填写预期成果。"}</p>
      <div className="project-card__progress">
        <div>
          <span>里程碑进度</span>
          <strong>{progress === null ? "待设置" : `${progress}%`}</strong>
        </div>
        <div className="progress-track">
          <i style={{ width: `${progress ?? 0}%` }} />
        </div>
      </div>
      <div className="project-card__footer">
        <span>{project.next_action ? `下一步：${project.next_action}` : "尚未定义下一步"}</span>
        <div className="project-card__actions">
          <Link className="button button--ghost button--sm" to={`/projects#project-${project.id}`}>
            <ArrowRight size={14} /> 当前项目
          </Link>
          <button className="icon-delete" onClick={() => void remove()} aria-label="删除项目">
            <Trash2 size={15} />
          </button>
        </div>
      </div>
    </article>
  );
}
